/* eslint-disable */
// TODO: Remove this once we define the expected behavior for Wolfie

import WolfFullTextarea from '@components/ui/WolfFullTextarea';
import { MinimizeIcon24 } from '@components/ui/WolfIcons';
import WolfTooltip from '@components/ui/WolfTooltip';
import { VisuallyHiddenInput } from '@components/VisuallyHiddenInput';
import { graphQlClient } from '@config/graphqlClient';
import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { getLastMessagesRequest } from '@models/user.model';
import { WolfieMessage } from '@models/wolfie.model';
import { AttachFileOutlined, BoltOutlined, HelpOutline, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useAppSelector } from '@redux/hooks';
import {
  restartWolfie,
  sendQuickCommand,
  setWolfieConversations,
  setWolfieEnabled,
  // setWolfieKnowledge,
  setWolfieLoading,
  setWolfieMessages,
  setWolfieMinimized,
  setWolfieNewMessages,
  setWolfieRateLimited,
  setWolfieRateLimitWarning,
  setWolfieRefreshSeparation,
  setWolfieShouldRefreshData
} from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import { waitForWolfieReady } from '@utils/ai/waitForWolfieReady';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import WolfieHub from './WolfieHub';
export interface WolfieProps {
  onTogglePosition?: () => void;
  isOnBottom?: boolean;
}

const Wolfie: React.FC<WolfieProps> = ({ onTogglePosition, isOnBottom = true }) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLDivElement | null>(null);
  const localeCommon = useTranslation('common');
  const localeWolfie = useTranslation('wolfie');

  const dispatch = useDispatch();
  const history = useHistory();

  // Helper function to format reset time
  const formatResetTime = (resetTimeStr: string | null): string => {
    if (!resetTimeStr) return '';

    const resetTime = new Date(resetTimeStr);
    const hours = resetTime.getHours();
    const minutes = resetTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const ALLOWED_WOLFIE_REDIRECTS = ['processes', 'areas', 'services'];
  const enabledWolfieVoice = process.env.REACT_APP_ENABLE_VOICE_WOLFIE === 'true';
  const enabledWolfieVideo = process.env.REACT_APP_ENABLE_VIDEO_WOLFIE === 'true';
  const [input, setInput] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [wolfieTalking, setWolfieTalking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [typedMessage, setTypedMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingStage, setLoadingStage] = useState(0);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [quickCommandsAnchor, setQuickCommandsAnchor] = useState<null | HTMLElement>(null);

  // 5 stages with 3 messages each to show progress
  const loadingMessageStages = [
    // Stage 1: Initial processing
    [
      localeWolfie['loadingStage1a'] || 'Reviewing your message...',
      localeWolfie['loadingStage1b'] || 'Understanding your request...',
      localeWolfie['loadingStage1c'] || 'Processing input...',
    ],
    // Stage 2: Gathering information
    [
      localeWolfie['loadingStage2a'] || 'Checking organization info...',
      localeWolfie['loadingStage2b'] || 'Gathering relevant data...',
      localeWolfie['loadingStage2c'] || 'Searching knowledge base...',
    ],
    // Stage 3: Analysis
    [
      localeWolfie['loadingStage3a'] || 'Analyzing current processes...',
      localeWolfie['loadingStage3b'] || 'Analyzing context...',
      localeWolfie['loadingStage3c'] || 'Reviewing policies...',
    ],
    // Stage 4: Processing
    [
      localeWolfie['loadingStage4a'] || 'Processing your request...',
      localeWolfie['loadingStage4b'] || 'Considering triggers...',
      localeWolfie['loadingStage4c'] || 'Evaluating options...',
    ],
    // Stage 5: Finalizing
    [
      localeWolfie['loadingStage5a'] || 'Preparing response...',
      localeWolfie['loadingStage5b'] || 'Finalizing answer...',
      localeWolfie['loadingStage5c'] || 'Almost ready...',
    ],
  ];

  // File processing specific loading messages
  const fileLoadingMessageStages = [
    [
      localeWolfie['fileLoadingStage1a'] || 'Reading your document...',
      localeWolfie['fileLoadingStage1b'] || 'Opening the file...',
      localeWolfie['fileLoadingStage1c'] || 'Scanning document contents...',
    ],
    [
      localeWolfie['fileLoadingStage2a'] || 'Extracting text from document...',
      localeWolfie['fileLoadingStage2b'] || 'Parsing document structure...',
      localeWolfie['fileLoadingStage2c'] || 'Analyzing document format...',
    ],
    [
      localeWolfie['fileLoadingStage3a'] || 'Identifying process steps...',
      localeWolfie['fileLoadingStage3b'] || 'Looking for procedures...',
      localeWolfie['fileLoadingStage3c'] || 'Detecting workflow patterns...',
    ],
    [
      localeWolfie['fileLoadingStage4a'] || 'Structuring the information...',
      localeWolfie['fileLoadingStage4b'] || 'Organizing extracted data...',
      localeWolfie['fileLoadingStage4c'] || 'Building process outline...',
    ],
    [
      localeWolfie['fileLoadingStage5a'] || 'Preparing your process...',
      localeWolfie['fileLoadingStage5b'] || 'Finalizing document analysis...',
      localeWolfie['fileLoadingStage5c'] || 'Almost ready with your process...',
    ],
  ];
  const {
    name: organizationName,
    description: organizationDescription,
    context: organizationContext,
    organizationId
  } = useAppSelector((state: RootState) => state.organization);
  const { avatar, rememberMe, role: userRole } = useAppSelector((state: RootState) => state.user);
  const {
    enabled: wolfieEnabled,
    showAttach,
    minimized,
    messages: wolfieMessages,
    loading: wolfieLoading,
    rateLimited: wolfieRateLimited,
    rateLimitResetTime: wolfieRateLimitResetTime,
    rateLimitWarning: wolfieRateLimitWarning,
    videoMode: wolfieVideoMode,
    speaking: wolfieSpeaking,
    newMessages: wolfieNewMessages,
    quickCommand,
  } = useAppSelector((state: RootState) => state.wolfie);

  // Set up resize observer for textarea
  useEffect(() => {
    if (!textareaRef.current) return;

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to avoid layout thrashing
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });

    resizeObserver.observe(textareaRef.current);

    // Cleanup on unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Scroll when new messages are added or minimized state changes
  useEffect(() => {
    // if (wolfieMessages.length > 0) {
    //   dispatch(setWolfieMessages([]));
    //   dispatch(setWolfieConversations([]));
    // }

    scrollToBottom();
  }, [wolfieMessages, minimized]);

  // Listen for quick command dispatched from other components
  useEffect(() => {
    if (quickCommand) {
      handleQuickCommand(quickCommand);
      // Clear the quick command after handling by setting it to empty string
      dispatch(sendQuickCommand(''));
    }
    }, [quickCommand]);

  // Auto-clear rate limit when time is reached
  useEffect(() => {
    if (!wolfieRateLimited) return;

    if (wolfieRateLimited && !wolfieRateLimitResetTime) {
      dispatch(setWolfieRateLimited(false, undefined));
      return;
    }

    let msUntilReset = 0;

    if (!wolfieRateLimitResetTime) {
      msUntilReset = 0;
    } else {
      const resetTime = new Date(wolfieRateLimitResetTime);
      const now = new Date();
      msUntilReset = resetTime.getTime() - now.getTime();
    }

    if (msUntilReset <= 0) {
      // Time already passed, clear immediately
      dispatch(setWolfieRateLimited(false, undefined));
      return;
    }

    // Set timer to clear rate limit when time is reached
    const timerId = setTimeout(() => {
      dispatch(setWolfieRateLimited(false, undefined));
    }, msUntilReset);

    return () => clearTimeout(timerId);
  }, [wolfieRateLimited, wolfieRateLimitResetTime, dispatch]);

  // Progress through loading message stages
  useEffect(() => {
    if (!wolfieLoading || wolfieNewMessages?.length) {
      setLoadingMessage('');
      setLoadingStage(0);
      setIsProcessingFile(false);
      return;
    }

    // Use file-specific messages when processing a file
    const messageStages = isProcessingFile ? fileLoadingMessageStages : loadingMessageStages;

    // Start at stage 0
    setLoadingStage(0);
    const randomIndex = Math.floor(Math.random() * 3);
    setLoadingMessage(messageStages[0][randomIndex]);

    // Progress through stages
    const interval = setInterval(() => {
      setLoadingStage(prev => {
        const nextStage = prev + 1;
        if (nextStage >= messageStages.length) {
          // Stay at final stage
          const randomIndex = Math.floor(Math.random() * 3);
          setLoadingMessage(messageStages[messageStages.length - 1][randomIndex]);
          return prev;
        }
        // Move to next stage
        const randomIndex = Math.floor(Math.random() * 3);
        setLoadingMessage(messageStages[nextStage][randomIndex]);
        return nextStage;
      });
    }, Math.random() * 2000 + 3000); // Random interval between 3-5 seconds

    return () => clearInterval(interval);
  }, [wolfieLoading, wolfieNewMessages, isProcessingFile]);

  useEffect(() => {
    if (wolfieSpeaking) {
      setWolfieTalking(false);
    } else {
      setWolfieTalking(false);
      speechSynthesis.cancel();
    }
  }, [wolfieSpeaking]);

  // Track last fetch timestamp to prevent rapid successive calls
  const lastFetchRef = useRef<number>(0);
  const FETCH_THROTTLE_MS = 5000; // 5 seconds minimum between fetches

  useEffect(() => {
    dispatch(setWolfieEnabled(true));
    lastFetchRef.current = Date.now();
    getLastMessages();
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      // Prevent fetching if a recent fetch was done (e.g., during login flow)
      const timeSinceLastFetch = Date.now() - lastFetchRef.current;
      if (timeSinceLastFetch < FETCH_THROTTLE_MS) {
        return;
      }
      lastFetchRef.current = Date.now();
      getLastMessages();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Clear conversation and fetch last messages when organization switches
  const organizationIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Skip on initial mount
    if (organizationIdRef.current === null) {
      organizationIdRef.current = organizationId;
      return;
    }

    // Only run when organizationId actually changes
    if (organizationIdRef.current !== organizationId) {
      organizationIdRef.current = organizationId;
      // Clear the current conversation
      dispatch(restartWolfie());
      // Fetch the last messages for the new organization
      lastFetchRef.current = Date.now();
      getLastMessages();
    }
  }, [organizationId]);

  useEffect(() => {
    if (wolfieNewMessages && wolfieNewMessages.length > 0) {
      setTypedMessage(' ');
      const message = wolfieNewMessages?.[0];

      if (message) {
        typeWords(message);
      }
    } else {
      setTypedMessage('');
      dispatch(setWolfieLoading(false));
    }
  }, [wolfieNewMessages]);

  const typeWords = async (message: WolfieMessage) => {
    const letters = message?.content?.split('');
    if (!letters) return;
    for (const letter of letters) {
      await new Promise((resolve) => setTimeout(resolve, 5)); //45));
      setTypedMessage((prev) => {
        const newMessage = prev + letter;
        requestAnimationFrame(() => {
          scrollToBottom();
        });
        return newMessage;
      });
    }
    dispatch(setWolfieMessages([...wolfieMessages, message]));
    dispatch(setWolfieNewMessages(wolfieNewMessages.slice(1)));
  };

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const onClose = () => {
    dispatch(setWolfieMinimized(true));
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const open = () => {
    dispatch(setWolfieMinimized(false));
  };

  const getLastMessages = async () => {
    try {
      const lastMessages: getLastMessagesRequest = await graphQlClient.request(UserGraphQL.queries.getLastMessages);

      const lastMessage = lastMessages.getLastMessages[lastMessages.getLastMessages.length - 1]?.content
        .split('---')
        .reverse()[0];
      const wolfieLastMessage = wolfieMessages[wolfieMessages.length - 1]?.content || '';

      if (lastMessage && lastMessage === wolfieLastMessage) {
        return;
      } else {
        const transformedMessages = lastMessages.getLastMessages.flatMap((message) => {
          const contentParts = message.content.split('---');
          return contentParts.map((contentPart: any) => ({
            messageId: uuidv4(),
            role: message.role,
            content: contentPart.trim(),
            createdAt: message.createdAt,
          }));
        }).filter((message: any) => message.content.length > 0); // Filter out empty messages
        dispatch(setWolfieMessages(transformedMessages));
      }
    } catch (error) {
      console.error('Error getting last messages:', error);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (wolfieLoading || wolfieNewMessages.length > 0) return;
    handleSubmit(e);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!input.trim()) return;
    dispatch(setWolfieLoading(true));
    setInput('');
    createAndSubmitMessage(input);
  };

  const handleSubmitAudio = (transcription: string) => {
    if (!transcription.trim()) return;
    createAndSubmitMessage(transcription);
  };

  const createAndSubmitMessage = (message: string) => {
    // Check if this is a restart command
    if (message.trim() === '/RESTART') {
      dispatch(restartWolfie());
    }

    const newMessage = {
      role: 'user',
      createdAt: new Date().toISOString(),
      content: message,
      id: Math.random().toString(36).slice(2, 20),
    };
    const updatedMessages = message.trim() === '/RESTART' ? [newMessage] : [...wolfieMessages, newMessage];
    dispatch(setWolfieMessages(updatedMessages));
    sendMessage(updatedMessages);
  };

  const handleQuickCommandsClick = (event: React.MouseEvent<HTMLElement>) => {
    setQuickCommandsAnchor(event.currentTarget);
  };

  const handleQuickCommandsClose = () => {
    setQuickCommandsAnchor(null);
  };

  const handleQuickCommand = (command: string) => {
    handleQuickCommandsClose();
    // Simulate form submission after a brief delay to allow input to update
    setTimeout(() => {
      createAndSubmitMessage(command);
    }, 50);
  };

  const handleCreateProcessFromFile = () => {
    handleQuickCommandsClose();
    fileInputRef.current?.click();
  };

  const sendMessage = async (updatedMessages: any) => {
    dispatch(setWolfieLoading(true));
    // Clear rate limit warning when sending a new message
    dispatch(setWolfieRateLimitWarning(false));
    const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
    let response;
    try {
      let newMessages: any[] = [];
      let data: any;
      try {
        const sessionUuid = getSessionUuid();

        response = await fetch(process.env.REACT_APP_BACKEND_URL + 'ai/v2/interaction', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Session-Uuid': sessionUuid,
            'X-Organization-Id': organizationId || ''
          },
          body: JSON.stringify({
            currentLocation: window.location.href.replace(window.location.origin, ''),
            message: updatedMessages[updatedMessages.length - 1].content.trim(),
          }),
        });
        data = await response.json();


      } catch (error) {
        dispatch(setWolfieLoading(false));
        const newMessage = {
          role: 'assistant',
          content: localeWolfie['processingError'],
          id: Math.random().toString(36).slice(2, 20),
        };

        dispatch(setWolfieMessages([...updatedMessages, newMessage]));

        console.error('Error sending message:', error);
        return;
      }

      try {
        if (!data?.message?.content) {
          newMessages = [
            {
              role: 'assistant',
              content: localeWolfie['responseError'],
              id: Math.random().toString(36).slice(2, 20),
            },
          ];
          dispatch(setWolfieLoading(false));
        } else {
          // Clear rate limit on successful response
          if (wolfieRateLimited) {
            dispatch(setWolfieRateLimited(false, undefined));
          }

          newMessages = data?.message?.content?.split('---').map((message: any) => ({
            role: 'assistant',
            content: message,
            id: Math.random().toString(36).slice(2, 20),
          }));

          // if (data?.metadata?.knowledge) {
          //   dispatch(setWolfieKnowledge(data.metadata.knowledge));
          // }

          // if (data?.metadata?.temporaryProcesses) {
          //   dispatch(setWolfieTemporaryProcesses(data.metadata.temporaryProcesses));
          // }

          if (!data?.metadata?.ignore && data?.metadata?.conversations) {
            dispatch(
              setWolfieConversations([
                ...data?.metadata?.conversations?.map((conversation: any) => ({
                  ...conversation,
                  status:
                    conversation?.conversationId === data.metadata.activeConversation?.conversationId
                      ? 'current'
                      : conversation.status,
                })),
              ]),
            );
          }

          // Check for rate limit warning/error
          if (data?.metadata?.rateLimit === 'warning') {
            dispatch(setWolfieRateLimitWarning(true));
          } else if (response.status === 429 || data?.statusCode === 429 || data?.metadata?.rateLimit === 'exceeded') {

            console.log('data dsadadas', data);

            
            // Get resetTime from response or calculate default (1 hour from now)
            const resetTime = data?.metadata?.resetTime || new Date(Date.now() + 60 * 60 * 1000).toISOString();
            dispatch(setWolfieRateLimited(true, resetTime));
          } else {
            // Clear warning if not present
            dispatch(setWolfieRateLimitWarning(false));
          }

          if (data?.metadata?.navigateTo) {
            history.push('/' + data.metadata.navigateTo);
          }

          if (data?.metadata?.shouldRefreshData) {
            if (data?.metadata?.refreshSeparation) {
              dispatch(setWolfieRefreshSeparation(data.metadata.refreshSeparation));
              dispatch(setWolfieShouldRefreshData(true));
            } else {
              dispatch(setWolfieShouldRefreshData(true));
            }
          }
        }
      } catch (error) {
        console.error('Error parsing response:', error);
        dispatch(setWolfieLoading(false));
        newMessages = [
          {
            role: 'assistant',
            content: localeWolfie['processingError'],
            id: Math.random().toString(36).slice(2, 20),
          },
        ];
      }

      if (!response || !wolfieEnabled) {
        return;
      }

      // Text-to-speech disabled
      // if (wolfieSpeaking) {
      //   playTextToSpeech(data?.message?.content.replace(/---/g, '. '), newMessages);
      // } else {
        // Wait for Wolfie to be ready before adding new messages
        dispatch(setWolfieLoading(false));
        await waitForWolfieReady();
        dispatch(setWolfieNewMessages([...newMessages]));
      // }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const playTextToSpeech = async (text: string, newMessages: any[]) => {
    console.log('playing text to speech', text);
    if (!text || text.length === 0) {
      return;
    }
    dispatch(setWolfieLoading(true));

    try {
      const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();

      const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'ai/speak', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Session-Uuid': getSessionUuid(),
        },
        body: JSON.stringify({ text }),
      });

      if (!response.body) throw new Error('No audio stream received');

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audio.play();
      setWolfieTalking(true);
      dispatch(setWolfieLoading(false));
      // Wait for Wolfie to be ready before adding new messages
      await waitForWolfieReady();
      dispatch(setWolfieNewMessages([...newMessages]));

      audio.onended = () => {
        setWolfieTalking(false);
        URL.revokeObjectURL(audioUrl); // Cleanup
      };
    } catch (error) {
      console.error('Error generating speech:', error);
      setWolfieTalking(false);
      dispatch(setWolfieLoading(false));
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsProcessingFile(true);
    dispatch(setWolfieLoading(true));
    setFileError(null);
    const selectedFile = e.target.files?.[0];

    // Reset input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Scroll to bottom so user can see the loading feedback
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    const maxSizeInBytes = 5 * 1024 * 1024;
    if (selectedFile) {
      if (selectedFile.size > maxSizeInBytes) {
        setFileError(localeWolfie['fileSizeError']);
        dispatch(setWolfieLoading(false));
        setIsProcessingFile(false);
        return;
      }

      const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
      const reader = new FileReader();
      reader.onloadend = async () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          dispatch(setWolfieLoading(false));
          setIsProcessingFile(false);
          return;
        }
        const base64File = result.split(',')[1];
        const payload = {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileContent: base64File,
        };

        try {
          const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'ai/uploadAIDocument', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Session-Uuid': getSessionUuid(),
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
          }

          const result = await response.json();

          if (result?.message?.content) {
            const newMessages = [
              ...wolfieMessages.filter((message: any) => message.id !== 'contextInfo'),
              {
                role: 'user',
                content: result?.message?.content,
                id: Math.random().toString(36).slice(2, 20),
              },
            ];
            // Send as user message - the summary node will recognize the document format
            dispatch(setWolfieMessages(newMessages));
            sendMessage(newMessages);
          } else {
            // No content in response - show error
            console.error('File upload: No content in response', result);
            const errorMessage = {
              role: 'assistant',
              content: localeWolfie['processingError'],
              id: Math.random().toString(36).slice(2, 20),
            };
            dispatch(setWolfieMessages([...wolfieMessages, errorMessage]));
            dispatch(setWolfieLoading(false));
            setIsProcessingFile(false);
          }
        } catch (error) {
          console.error('File upload error:', error);
          const newMessage = {
            role: 'assistant',
            content: localeWolfie['processingError'],
            id: Math.random().toString(36).slice(2, 20),
          };
          dispatch(setWolfieMessages([...wolfieMessages, newMessage]));
          dispatch(setWolfieLoading(false));
          setIsProcessingFile(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFileError(localeWolfie['fileRequiredError']);
      dispatch(setWolfieLoading(false));
      setIsProcessingFile(false);
      return;
    }
  };

  const handleOptionSelect = (event: any) => {
    const selectedOption = event.target.value;
    setInput(selectedOption);
  };

  if (!wolfieEnabled) {
    return null;
  }

  return (
    <Box className="flex flex-col gap-4 items-end">
      {/* <Box>{wolfieVideoMode && <InteractiveAvatar messages={wolfieMessages} />}</Box> */}
      {!minimized && (
        <Box className="relative">
          <Box
            className="bg-white shadow-md p-4 rounded-lg overflow-y-auto w-full"
            sx={{ width: '450px', height: expanded ? '80vh' : '60vh' }}>
            <Box className="flex items-center gap-2 border-0 border-b-primaryIcy border-b border-solid justify-end absolute top-0 right-0 left-0 h-12 px-4">
              <WolfTooltip title={localeWolfie['attachFileTooltip']}>
                <IconButton component="label" disabled={wolfieLoading}>
                  <AttachFileOutlined fontSize="small" />
                  <VisuallyHiddenInput
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".doc,.docx,.txt,.pdf"
                    disabled={wolfieLoading}
                  />
                </IconButton>
              </WolfTooltip>
              <WolfTooltip title={localeWolfie['quickCommands']}>
                <IconButton onClick={handleQuickCommandsClick}>
                  <BoltOutlined fontSize="small" />
                </IconButton>
              </WolfTooltip>
              <Menu
                anchorEl={quickCommandsAnchor}
                open={Boolean(quickCommandsAnchor)}
                onClose={handleQuickCommandsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}>
                <MenuItem onClick={() => handleQuickCommand(localeWolfie['quickCommandCreateProcess'])}>
                  {localeWolfie['quickCommandCreateProcess']}
                </MenuItem>
                <MenuItem onClick={handleCreateProcessFromFile}>
                  {localeWolfie['quickCommandCreateProcessFromFile']}
                </MenuItem>
                <MenuItem onClick={() => handleQuickCommand(localeWolfie['quickCommandWritePolicy'])}>
                  {localeWolfie['quickCommandWritePolicy']}
                </MenuItem>
                <MenuItem onClick={() => handleQuickCommand(localeWolfie['quickCommandKnowOrganization'])}>
                  {localeWolfie['quickCommandKnowOrganization']}
                </MenuItem>
                <MenuItem onClick={() => handleQuickCommand(localeWolfie['quickCommandRestart'])}>
                  {localeWolfie['quickCommandRestart']}
                </MenuItem>
              </Menu>
              <WolfTooltip title={localeWolfie['helpTooltip']}>
                <IconButton>
                  <HelpOutline fontSize="small" />
                </IconButton>
              </WolfTooltip>
              <WolfTooltip title={expanded ? localeWolfie['collapse'] : localeWolfie['expand']}>
                <IconButton onClick={toggleExpand} className="rotate-45">
                  {expanded ? <UnfoldLess /> : <UnfoldMore />}
                </IconButton>
              </WolfTooltip>
              <WolfTooltip title={localeWolfie['minimize']}>
                <IconButton onClick={onClose}>
                  <MinimizeIcon24 />
                </IconButton>
              </WolfTooltip>
            </Box>
            <Box className="flex flex-col h-full gap-1">
              <Box
                className={
                  'flex-1 flex flex-wrap h-0 overflow-y-auto mt-12 pr-4 ' +
                  (expanded ? 'max-h-wolfie-expanded ' : 'max-h-wolfie ')
                }>
                <Box className="flex flex-col gap-1 overflow-text-wrap w-full h-full">
                  {wolfieMessages
                    .filter((message: any) => message.role !== 'system' && message.id !== 'contextInfo' && message.content?.trim().length > 0)
                    .map((message: any, index: number) => {
                      const isUser = message.role?.toLowerCase() === 'user';
                      let displayMessage = message.content;
                      return (
                        <Box className="w-full h-fit max-h-fit flex" key={index}>
                          <Box className={'flex gap-1 ' + (isUser ? 'ml-auto' : '')}>
                            {isUser ? <></> : <></>}
                            {isUser ? (
                              <Box className="bg-white border-primaryIcy border border-solid p-1 rounded ml-12">
                                <Typography variant="body16" color="primary">
                                  <ReactMarkdown>{displayMessage}</ReactMarkdown>
                                </Typography>
                              </Box>
                            ) : (
                              <Box className="bg-backgroundUI border-primaryIcy border border-solid p-1 rounded mr-12">
                                <Typography variant="body16" color="primary">
                                  <ReactMarkdown>{displayMessage}</ReactMarkdown>
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  {typedMessage.trim() && (
                    <Box>
                      <Box className="bg-backgroundUI border-primaryIcy border border-solid p-1 rounded mr-12">
                        <Typography variant="body16" color="primary">
                          {typedMessage.trim()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {wolfieLoading && !wolfieNewMessages?.length && (
                    <Box className="flex items-center space-x-3 m-3 py-2 mb-5">
                      <Box className="flex space-x-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-ping [animation-delay:0ms]"></span>
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-ping [animation-delay:100ms]"></span>
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-ping [animation-delay:300ms]"></span>
                      </Box>
                      <Typography variant="body14" className="text-gray-600 italic">
                        {loadingMessage}
                      </Typography>
                    </Box>
                  )}
                  {wolfieRateLimitWarning && !wolfieRateLimited && (
                    <Box className="flex items-center m-3 py-2 px-3 mb-5 bg-amber-50 border border-amber-300 rounded">
                      <Typography variant="body14" className="text-amber-700">
                        {localeWolfie['rateLimitWarning']}
                      </Typography>
                    </Box>
                  )}
                  {wolfieRateLimited && wolfieRateLimitResetTime && (
                    <Box className="flex items-center m-3 py-2 px-3 mb-5 bg-amber-50 border border-amber-300 rounded">
                      <Typography variant="body14" className="text-amber-700">
                        {localeWolfie['rateLimitError']?.replace('{time}', formatResetTime(wolfieRateLimitResetTime))}
                      </Typography>
                    </Box>
                  )}
                  <Box ref={bottomRef} sx={{ scrollMargin: 100 }} className="leading-[1px]">
                    <span className="h-20 min-h-20">&nbsp;</span>
                  </Box>
                </Box>
              </Box>
              <form onSubmit={handleFormSubmit} className="mt-auto">
                <Box className="flex border-t-primaryIcy border-0 border-t border-solid mt-0">
                  <Box className="w-full pt-2" ref={textareaRef}>
                    <WolfFullTextarea
                      value={input}
                      disabled={wolfieRateLimited}
                      onChange={(e) => setInput(e.target.value)}
                      onSubmit={() => {
                        if (!input.trim()) return;
                        if (wolfieLoading || wolfieRateLimited || wolfieNewMessages.length > 0) return;
                        handleSubmit({ preventDefault: () => {} } as React.FormEvent);
                      }}
                      placeholder={localeWolfie['messagePlaceholder']}
                      autoFocus
                    />
                  </Box>
                </Box>
              </form>
            </Box>

            {/* TODO: Remove buttons & Re-add attach? */}
            {/* <Box className="flex items-center gap-2 absolute bottom-0 left-0 right-0 bg-primaryFrozen">
            <Box className="p-2 flex items-center gap-2 justify-end">
              <WolfButton type="submit" disabled={wolfieLoading} color="secondary" variant="outlined">
                {localeCommon['send']}
              </WolfButton>
              {(userRole === UserRoles.SUPER_ADMIN || userRole === UserRoles.OWNER) && enabledWolfieVoice && (
              )}
              {showAttach && (
                <WolfTooltip title={localeCommon['clickToAttachWolfie']}>
                  <span>
                    <WolfButton
                      color="secondary"
                      variant="outlined"
                      component="label"
                      tabIndex={-1}
                      disabled={wolfieLoading}>
                      <VisuallyHiddenInput
                        type="file"
                        onChange={handleFileChange}
                        accept=".doc,.docx,.txt,.pdf"
                        disabled={wolfieLoading}
                      />
                      <AttachFileOutlined />
                    </WolfButton>
                  </span>
                </WolfTooltip>
              )}
            </Box>
          </Box> */}
            {/* {wolfieData && wolfieData.length > 0 && (
                <Box className="absolute bottom-28 right-4 flex items-center gap-2">
                  <Typography variant="body16">Options:</Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value=""
                      onChange={handleOptionSelect}
                      displayEmpty
                      variant="outlined"
                      sx={{ backgroundColor: 'white' }}>
                      <MenuItem value="" disabled>
                        <em>Select an option</em>
                      </MenuItem>
                      {wolfieData.map((item: any, index: number) => (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )} */}
          </Box>
        </Box>
      )}
      <Box className="">
        <WolfieHub isTalking={wolfieTalking} onSubmit={handleSubmitAudio} onTogglePosition={onTogglePosition} isOnBottom={isOnBottom} />
      </Box>
    </Box>
  );
};

export default Wolfie;
